import { TFunction } from "i18next";
import { type ColumnDef } from "~/components/table";
import { Checkbox } from "~/components/ui/checkbox";
import { TruncatedText } from "~/components/truncated-text";
import { Text } from "~/components/text";

import { TableStatusBadges } from "~/components/generation-status-badge/table-status-badges";
import { InfoTooltip } from "~/components/info-tooltip";
import { formatSearchScore } from "~/hooks/search/adapters";
import { SearchProductWithScore } from "~/hooks/search/types";
import { DataTableColumnHeader } from "~/components/ui/data-table-column-header";

export const columns: (
  t: TFunction,
  options?: {
    showMatch: boolean;
    loading: boolean;
  },
) => ColumnDef<SearchProductWithScore>[] = (t, options) => [
  {
    id: "select",
    ignoreColumnClick: true,
    size: 50,
    header: ({ table }) =>
      options?.loading ? null : (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
            table.setLastSelectedRowId(null);
          }}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onClick={(event) => row.toggleSelectedWithRange(event.shiftKey)}
        aria-label="Select row"
        className="translate-y-[2px] select-none"
      />
    ),
  },
  {
    accessorKey: "isbn",
    size: 112,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.isbn")}
      />
    ),
    cell: ({ row }) => <div>{row.getValue("isbn")}</div>,
  },
  {
    accessorKey: "author",
    size: 120,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.author")}
      />
    ),
    cell: ({ row }) => {
      const authors = row.getValue("author") as string[];

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={authors?.join(", ")} />
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    size: 240,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.title")}
      />
    ),
    cell: ({ row }) => (
      <div className="max-w-[500px] flex space-x-2">
        <TruncatedText text={row.getValue("title")} />
      </div>
    ),
  },
  {
    accessorKey: "productGroupDescription",
    size: 120,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.productForm")}
      />
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <span> {row.getValue("productGroupDescription")}</span>
      </div>
    ),
  },
  {
    accessorKey: "publishers",
    size: 80,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.publishedBy")}
      />
    ),
    cell: ({ row }) => {
      const publishers = row.getValue("publishers") as
        | { name: string; role: string | null }[]
        | undefined;
      const publisher = row.original.publisher as string | undefined;

      let publisherName = "";

      if (publishers && publishers.length > 0) {
        publisherName = publishers.map((p) => p.name).join(", ");
      } else if (publisher) {
        publisherName = publisher;
      }

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={publisherName} />
        </div>
      );
    },
  },
  {
    accessorKey: "imprints",
    size: 80,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.imprint")}
      />
    ),
    cell: ({ row }) => {
      const imprints = row.getValue("imprints") as string[] | undefined;
      const imprintName = imprints?.join(", ") || "-";

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={imprintName} />
        </div>
      );
    },
  },
  {
    accessorKey: "publishedAt",
    size: 160,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.publishedOn")}
      />
    ),
    cell: ({ row }) => {
      const publishedAt = row.getValue("publishedAt") as Date | null;
      return (
        <div className="flex items-center">
          <span>{publishedAt?.toISOString().slice(0, 10)}</span>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  ...(options?.showMatch
    ? [
        {
          accessorKey: "score",
          size: 80,
          header: ({ column }) => (
            <div className="flex items-center gap-2">
              <DataTableColumnHeader
                column={column}
                title={t("books:booktable.columns.score")}
              />
              <InfoTooltip side="top">
                <div className="font-light text-center px-5">
                  <Text variant="body" className="whitespace-pre">
                    {t("books:booktable.scoreTooltip.text")}
                  </Text>
                  <Text variant="subtleItalic" className="whitespace-pre">
                    {t("books:booktable.scoreTooltip.note")}
                  </Text>
                </div>
              </InfoTooltip>
            </div>
          ),
          cell: ({ row }) => {
            const score = row.getValue("score") as number | undefined;
            return (
              <div className="flex items-center justify-center">
                <span data-testid={`book-score-${row.original.id}`}>
                  {formatSearchScore(score)}
                </span>
              </div>
            );
          },
        } as ColumnDef<SearchProductWithScore>,
      ]
    : []),
  {
    accessorKey: "generated",
    size: 80,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("books:booktable.columns.generated")}
      />
    ),
    cell: ({ row }) => {
      return <TableStatusBadges row={row} fetch />;
    },
  },
];
